import { useRef } from "react";

import {
    ToolbarSpacer,
    TSTAdjustView,
    TSTTable,
    TableToolbar,
    TSTFilterBar,
    TextTool,
    TSTPagination,
} from "@impero-com/ui";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";

import { ArrayOfTActTableRowDto, TActTableRow } from "api/generated/model";
import { useCurrentUserTimezone } from "utils/currentTimezone";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { ArchiveTestingActivitiesModal } from "./ArchiveTestingActivitiesModal";
import { TestingActivityExportModal } from "./components/TestingActivityExportModal";
import { ReassignTestingActivitiesModal } from "./ReassignTestingActivitiesModal";
import { TestingActivityArchivedFilterRow } from "./TestingActivityArchivedFilterRow";
import { useArchiveUtils } from "./useArchiveUtils";
import { useReassignUtils } from "./useReassignUtils";
import { TestingActivityArchivedFilterResult } from "./useTestingActivityArchivedFilter";
import { useTestingActivityExportUtils } from "./useTestingActivityExportUtils";
import {
    TestingActivityListSearchParams,
    useTestingActivityListTable,
} from "./useTestingActivityListTable";

interface TestingActivityListForTestingProgramProps {
    data: ArrayOfTActTableRowDto;
    testingProgramId: number;
    archivedFilter: TestingActivityArchivedFilterResult;
    searchParams: TestingActivityListSearchParams;
    isTableLoading: boolean;
}

export function TestingActivityForTestingProgramList({
    data: sourceData,
    testingProgramId,
    archivedFilter,
    searchParams,
    isTableLoading,
}: TestingActivityListForTestingProgramProps) {
    const { t } = useTranslation();
    const timezone = useCurrentUserTimezone();
    const data = sourceData.content.resource;
    const actions = sourceData.content.actions;

    const { table, getFilterComponent, canAddException, tableId } =
        useTestingActivityListTable({
            data,
            timezoneName: {
                name: timezone,
            },
            relatedResources: sourceData.linkedResources,
            testingProgramId,
            searchParams,
        });

    const selectedRows = table.getSelectedRowModel().rows;
    const visibleColumns = table
        .getAllColumns()
        .filter((column) => column.getIsVisible());
    const selectedTestingActivityIds = selectedRows.map(
        (row) => row.original.activityId
    );

    const {
        exportToolState,
        exportModalOpen,
        openExportModal,
        closeExportModal,
        handleExport,
        isExporting,
    } = useTestingActivityExportUtils({
        selectedRows,
        testingProgramId,
        visibleColumns,
    });

    const {
        reassignToolState,
        reassignModalOpened,
        maybeCurrentStep,
        setReassignModalOpened,
    } = useReassignUtils({
        selectedRows,
        actions,
    });

    const { archiveToolState, archiveModalOpened, setArchiveModalOpened } =
        useArchiveUtils({ selectedRows });

    const tableRef = useRef<HTMLTableElement>(null);

    const modeConfig = getFilterModeConfig(t);

    return (
        <div>
            <TestingActivityExportModal
                onClose={closeExportModal}
                onSubmit={(type) => void handleExport(type)}
                open={exportModalOpen}
                loading={isExporting}
            />
            {maybeCurrentStep && (
                <ReassignTestingActivitiesModal
                    open={reassignModalOpened}
                    testingActivityIds={selectedTestingActivityIds}
                    currentStep={maybeCurrentStep}
                    close={() => {
                        setReassignModalOpened(false);
                    }}
                    deselectAll={() => {
                        table.toggleAllPageRowsSelected(false);
                    }}
                    hasReviewStep={
                        selectedRows[0]?.original.firstReviewInfo !== null
                    }
                    hasSecondReviewStep={
                        selectedRows[0]?.original.secondReviewInfo !== null
                    }
                />
            )}
            <ArchiveTestingActivitiesModal
                open={archiveModalOpened}
                testingActivityIds={selectedTestingActivityIds}
                testingProgramId={testingProgramId}
                close={() => {
                    setArchiveModalOpened(false);
                }}
                deselectAll={() => {
                    table.toggleAllPageRowsSelected(false);
                }}
            />
            <TSTFilterBar<TActTableRow>
                table={table}
                tableId={tableId}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
                canAddException={canAddException}
                addExceptionLabel={t("FilterBar_AddException")}
                modeConfig={modeConfig}
                belowFilters={
                    <TestingActivityArchivedFilterRow
                        archivedFilters={archivedFilter}
                    />
                }
                extraFilterCount={archivedFilter.isNonDefault ? 1 : 0}
                onClearFilters={searchParams.clearAllFilters}
            />
            <Spin spinning={isTableLoading} size="large">
                <TSTTable
                    tableRef={tableRef}
                    table={table}
                    noResultsFallback={t("NoResults")}
                    noSourceRowsFallback={t("NoResults")}
                    toolbar={
                        <TableToolbar>
                            <TextTool
                                state={exportToolState}
                                onClick={openExportModal}
                            >
                                {t("TestingActivities_Export")}
                            </TextTool>
                            <TextTool
                                state={reassignToolState}
                                onClick={() => {
                                    setReassignModalOpened(true);
                                }}
                            >
                                {t("TestingActivities_Reassign")}
                            </TextTool>
                            <TextTool
                                state={archiveToolState}
                                onClick={() => {
                                    setArchiveModalOpened(true);
                                }}
                            >
                                {t("TestingActivities_Archive")}
                            </TextTool>
                            <ToolbarSpacer />
                            <TSTAdjustView
                                table={table}
                                columnsLabel={t("AdjustView_ColumnsHeader")}
                                reorderHintLabel={t(
                                    "AdjustView_DragToReorderHint"
                                )}
                                resetLabel={t("AdjustView_Reset")}
                                pinLabel={t("AdjustView_PinColumn")}
                                unpinLabel={t("AdjustView_UnpinColumn")}
                                containerRef={tableRef}
                            >
                                {t("ControlResult_AdjustView")}
                            </TSTAdjustView>
                        </TableToolbar>
                    }
                />
            </Spin>
            <TSTPagination
                table={table}
                tableElement={tableRef}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
            />
        </div>
    );
}
